/**
 * A kind pill (TMMAPP-0088, pattern 8; TMMAPP-0099, visual contract §9): radius 11, padding 3 9,
 * `micro` (10/500, 0.06em, uppercase). The label is the caller's catalog string. A financial kind
 * paints `toneForKind` through `toneColours`, or its state layer (`KIND_LAYER`: Cash, Asset); a
 * flag passes `neutral` (`text` on `surface2`). Never outlined — the only outlined chip is a draft
 * status.
 */
import { forwardRef } from 'react';
import { Chip, useTheme, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { layerOverSurface, resolveTokens } from '@/theme/theme';
import { toneChipSx } from '@/theme/toneChipSx';
import { KIND_LAYER, toneForKind, type Kind } from './kindTones';

type KindChipProps = {
  label: string;
  size?: 'small' | 'medium';
  component?: 'div' | 'span';
  sx?: SystemStyleObject<Theme>;
} & ({ kind: Kind } | { neutral: true });

/** The pill's shape and type: the same for every kind. */
const PILL_SX = {
  height: 'auto',
  minHeight: 0,
  borderRadius: '11px',
  '& .MuiChip-label': {
    padding: '3px 9px',
    fontSize: 10,
    lineHeight: '14px',
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
} as const;

export const KindChip = forwardRef<HTMLDivElement, KindChipProps>(function KindChip(props, ref) {
  const { label, size = 'small', component, sx } = props;
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const tone = 'neutral' in props ? 'neutral' : toneForKind(props.kind);
  const layered = 'kind' in props ? KIND_LAYER[props.kind] : undefined;
  const paint =
    layered === undefined
      ? toneChipSx(tokens, tone)
      : (() => {
          const ink = tokens[layered.ink];
          const fill = { background: layerOverSurface(tokens, tokens[layered.layer]), color: ink };
          // `background` (the layer over an opaque surface) and the label ink beat MUI's filled
          // variant as `sx`; no specificity override (TMMAPP-0108).
          return { ...fill, '& .MuiChip-label': { color: ink } };
        })();
  return (
    <Chip
      ref={ref}
      {...(component === undefined ? {} : { component })}
      size={size}
      variant="filled"
      label={label}
      data-tone={tone}
      data-kind={'kind' in props ? props.kind : undefined}
      data-layer={layered?.layer}
      sx={{
        ...PILL_SX,
        ...paint,
        '& .MuiChip-label': { ...PILL_SX['& .MuiChip-label'], ...paint['& .MuiChip-label'] },
        ...sx,
      }}
    />
  );
});
